import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Platform,
  Linking,
  ActivityIndicator,
  ScrollView,
  StyleSheet,
} from 'react-native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import {
  PLAN_IDS,
  SUBSCRIPTION_PLANS,
  formatCentsAsDollars,
  getPlanById,
} from '../constants/subscriptionPlans';
import {
  formatFundsCents,
  convertCreditsCentsToWalletCents,
  formatCreditsAmount,
  formatCreditExchangeRateLine,
} from '../constants/userFunds';
import { useTheme } from '../contexts/ThemeContext';
import { getDeployTarget } from '../config/environment';
import { useUser } from '../components/LoginPersistence';
import { useCreditEconomy } from '../contexts/CreditEconomyContext';
import { hasPaidProductAccess, isSubscriptionActive } from '../utils/subscriptionEntitlements';
import { getPrimaryTourPartyCoverage, hasTourPartyCrewAccess } from '../utils/tourPartySubscription';
import { palette, FONT } from '../styles/tourTheme';
import { glossyFill, glossyWebStyle } from '../utils/calendarV2Prefs';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (_) {
    return '—';
  }
}

function statusLabel(data) {
  if (!data) return 'Unknown';
  if (data.cancelAtPeriodEnd) return 'Cancels at period end';
  if (data.status === 'active') return 'Active';
  if (data.status === 'cancelled') return 'Cancelled';
  return data.status || data.stripeStatus || 'Unknown';
}

function creditsHistorySigned(entry) {
  const n = Math.max(0, Number(entry.amountCents) || 0);
  if (entry.type === 'earn' || entry.type === 'transfer_in') return n;
  if (entry.type === 'billing_applied'
    || entry.type === 'donate'
    || entry.type === 'transfer_out'
    || entry.type === 'exchange_to_wallet'
  ) {
    return -n;
  }
  return 0;
}

export function ManageSubscription({ navigation, embedded = false }) {
  const { isDarkMode } = useTheme();
  const { user, setUser } = useUser();
  const creditEconomy = useCreditEconomy();
  const pal = palette(isDarkMode);
  const accent = '#4e7de0';
  const gloss = glossyFill(accent, { isDarkMode, role: 'panel' });

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const syncUserFromManageData = useCallback((payload) => {
    if (!payload || !setUser) return;
    setUser((prev) => ({
      ...prev,
      subscriptionPlan: payload.planId ?? prev.subscriptionPlan,
      subscriptionStatus: payload.status ?? prev.subscriptionStatus,
      subscriptionCancelAt: payload.cancelAt ?? prev.subscriptionCancelAt,
      subscriptionApprovedLocationsThisCycle:
        payload.approvedCount ?? prev.subscriptionApprovedLocationsThisCycle,
      subscriptionDiscountCentsThisCycle:
        payload.discountCents ?? prev.subscriptionDiscountCentsThisCycle,
      subscriptionEffectivePriceCentsThisCycle:
        payload.effectivePriceCents ?? prev.subscriptionEffectivePriceCentsThisCycle,
      subscriptionBillingCycleStart:
        payload.periodStart ?? prev.subscriptionBillingCycleStart,
      subscriptionBillingCycleEnd:
        payload.periodEnd ?? prev.subscriptionBillingCycleEnd,
      stripeMode: payload.stripeMode ?? prev.stripeMode,
      fundsBalanceCents:
        payload.creditsBalanceCents != null
          ? payload.creditsBalanceCents
          : prev.fundsBalanceCents,
    }));
  }, [setUser]);

  const loadManageData = useCallback(async () => {
    const auth = getAuth();
    if (!auth.currentUser) {
      navigation.navigate('Login');
      return;
    }

    setError('');
    setLoading(true);
    try {
      const functions = getFunctions();
      const getSubscriptionManageData = httpsCallable(functions, 'getSubscriptionManageData');
      const result = await getSubscriptionManageData({
        deployTarget: getDeployTarget(),
      });
      const payload = result?.data || null;
      setData(payload);
      syncUserFromManageData(payload);
    } catch (err) {
      console.error('Manage subscription load error:', err);
      setError(err?.message || 'Could not load subscription details.');
    } finally {
      setLoading(false);
    }
  }, [navigation, syncUserFromManageData]);

  useEffect(() => {
    loadManageData();
  }, [loadManageData]);

  const runAction = async (key, fn) => {
    setError('');
    setActionLoading(key);
    try {
      await fn();
      await loadManageData();
    } catch (err) {
      console.error(`Manage subscription action ${key} failed:`, err);
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = () =>
    runAction('cancel', async () => {
      const functions = getFunctions();
      const cancelSubscription = httpsCallable(functions, 'cancelSubscription');
      await cancelSubscription({ deployTarget: getDeployTarget() });
    });

  const handleResume = () =>
    runAction('resume', async () => {
      const functions = getFunctions();
      const resumeSubscription = httpsCallable(functions, 'resumeSubscription');
      await resumeSubscription({ deployTarget: getDeployTarget() });
    });

  const handleUpdatePayment = () =>
    runAction('payment', async () => {
      const origin =
        Platform.OS === 'web' && typeof window !== 'undefined' && window.location?.origin
          ? window.location.origin
          : null;
      const functions = getFunctions();
      const createSetupCheckoutSession = httpsCallable(functions, 'createSetupCheckoutSession');
      const result = await createSetupCheckoutSession({
        deployTarget: getDeployTarget(),
        ...(origin
          ? {
              successUrl: `${origin}/manage-subscription`,
              cancelUrl: `${origin}/manage-subscription`,
            }
          : {}),
      });
      const url = result?.data?.url;
      if (!url) throw new Error('Checkout URL was not returned.');
      if (Platform.OS === 'web') {
        window.location.href = url;
      } else {
        await Linking.openURL(url);
      }
    });

  const handleExchangeCredits = () =>
    runAction('exchange', async () => {
      const functions = getFunctions();
      const exchangeCreditsToWallet = httpsCallable(functions, 'exchangeCreditsToWallet');
      await exchangeCreditsToWallet({});
    });

  const openInvoice = async (invoice) => {
    const url = invoice?.hostedInvoiceUrl || invoice?.invoicePdf;
    if (!url) return;
    if (Platform.OS === 'web') {
      window.open(url, '_blank');
    } else {
      await Linking.openURL(url);
    }
  };

  const plan = getPlanById(data?.planId);
  const tourPartyCoverage = useMemo(() => getPrimaryTourPartyCoverage(user), [user]);
  const coveredByTourAdmin = hasTourPartyCrewAccess(user);
  const hasOwnSubscription = isSubscriptionActive(user)
    || data?.status === 'active'
    || (data?.cancelAtPeriodEnd && data?.cancelAt && new Date(data.cancelAt) > new Date());
  const hasActiveAccess = hasOwnSubscription || coveredByTourAdmin || hasPaidProductAccess(user);

  const creditsCents = Math.max(0, Number(data?.creditsBalanceCents) || 0);
  const memberPriceCents = SUBSCRIPTION_PLANS[PLAN_IDS.VRC_MEMBER].priceCents;
  const maxDiscountCents = data?.maxDiscountCents != null
    ? Math.max(0, Number(data.maxDiscountCents) || 0)
    : creditEconomy.maxSubscriptionDiscountCents(memberPriceCents);
  // Both in Credits (formatCreditsAmount): Credits the next invoice uses, of the per-cycle max.
  const maxApply = creditEconomy.creditsForDiscountCents(maxDiscountCents);
  const nextApplyCents = creditEconomy.subscriptionDiscountForCredits(creditsCents, memberPriceCents).creditsUsed;
  const lastApplied = data?.lastCreditsApplied || null;
  const creditsHistory = Array.isArray(data?.creditsHistory) ? data.creditsHistory : [];

  const applyProgress = useMemo(() => {
    if (!maxApply) return 0;
    return Math.min(1, nextApplyCents / maxApply);
  }, [nextApplyCents, maxApply]);

  const exchangeableCreditsBalance = useMemo(
    () => creditEconomy.capCreditsBalanceForMonthlyExchange(creditsCents),
    [creditEconomy, creditsCents],
  );

  const exchangePreviewWalletCents = useMemo(
    () => creditEconomy.convertCreditsCentsToWalletCents(exchangeableCreditsBalance),
    [creditEconomy, exchangeableCreditsBalance],
  );

  return (
    <View style={[styles.page, embedded ? { backgroundColor: 'transparent', flex: 1 } : { backgroundColor: pal.page }]}>
      <ScrollView contentContainerStyle={[styles.pageInner, embedded && { paddingHorizontal: 0, paddingTop: 8 }]}>
        {!embedded ? (
          <TouchableOpacity
            onPress={() => navigation.navigate('Wallet', { tab: 'wallet' })}
            style={{ marginBottom: 14, alignSelf: 'flex-start' }}
          >
            <Text style={{ color: accent, fontWeight: '600', fontSize: 15 }}>
              ← Wallet & payouts
            </Text>
          </TouchableOpacity>
        ) : null}
        {error ? (
          <View style={[styles.banner, { backgroundColor: pal.alert }]}>
            <Text style={{ color: '#fff', fontSize: 14.5 }}>{error}</Text>
          </View>
        ) : null}

        <View style={[styles.heroRow, { borderBottomColor: accent }]}>
          <View style={{ flex: 1.2, minWidth: 240 }}>
            <Text style={[s.label, { color: pal.mute, marginBottom: 6 }]}>Subscription</Text>
            <Text style={[s.hero, { color: pal.ink, fontFamily: FONT.sans }]}>Credits & plan</Text>
            <Text style={{ color: pal.mute, marginTop: 8, fontSize: 14.5, lineHeight: 21, maxWidth: 400 }}>
              VRC Member is {formatCentsAsDollars(3300)}/mo. Credits bank from map contributions — exchange to Wallet monthly.
            </Text>
          </View>

          <View
            style={[
              styles.accentPanel,
              {
                borderColor: gloss.borderColor || pal.line,
                borderLeftColor: accent,
                ...(Platform.OS === 'web'
                  ? glossyWebStyle(gloss)
                  : { backgroundColor: gloss.backgroundColor }),
              },
            ]}
          >
            <Text style={[s.label, { color: gloss.mute, marginBottom: 10 }]}>Credits</Text>
            <Text style={{ fontSize: 36, fontWeight: '600', color: gloss.ink, letterSpacing: -0.8 }}>
              {loading && !data ? '…' : formatCreditsAmount(creditsCents)}
            </Text>
            <Text style={{ color: gloss.mute, fontSize: 13.5, marginTop: 4 }}>
              {creditEconomy.formatCreditExchangeRateLine()}
            </Text>
            <Text style={{ color: gloss.mute, fontSize: 12.5, marginTop: 4 }}>
              {creditEconomy.formatMaxCreditsExchangePerMonthLine()}
            </Text>
            {creditsCents > 0 ? (
              <Text style={{ color: gloss.mute, fontSize: 13, marginTop: 4 }}>
                Exchange preview: {formatCreditsAmount(exchangeableCreditsBalance)} →{' '}
                {formatFundsCents(exchangePreviewWalletCents)} Wallet
              </Text>
            ) : null}
            <TouchableOpacity
              onPress={handleExchangeCredits}
              disabled={!!actionLoading || exchangeableCreditsBalance < 1}
              style={[
                styles.primaryBtn,
                {
                  backgroundColor: gloss.ink,
                  marginTop: 12,
                  opacity: exchangeableCreditsBalance < 1 || actionLoading ? 0.5 : 1,
                },
              ]}
            >
              {actionLoading === 'exchange' ? (
                <ActivityIndicator color={gloss.page || '#fff'} />
              ) : (
                <Text style={{ color: accent || '#000', fontWeight: '600', textAlign: 'center' }}>
                  Exchange Credits to Wallet 
                </Text>
              )}
            </TouchableOpacity>
            <Text style={{ color: gloss.mute, fontSize: 13.5, marginTop: 10 }}>
              Next invoice apply · {formatCreditsAmount(nextApplyCents)} of{' '}
              {formatCreditsAmount(maxApply)}
            </Text>
            <View style={[styles.progressTrack, { backgroundColor: gloss.line || pal.line }]}>
              <View
                style={[
                  styles.progressFill,
                  {
                    width: `${Math.round(applyProgress * 100)}%`,
                    backgroundColor: gloss.ink,
                  },
                ]}
              />
            </View>
            {lastApplied ? (
              <Text style={{ color: gloss.mute, fontSize: 12.5, marginTop: 10 }}>
                Last used {formatCreditsAmount(lastApplied.amountCents)}
                {lastApplied.createdAt ? ` · ${formatDate(lastApplied.createdAt)}` : ''}
              </Text>
            ) : (
              <Text style={{ color: gloss.mute, fontSize: 12.5, marginTop: 10 }}>
                Not applied to an invoice yet
              </Text>
            )}
          </View>
        </View>

        {loading && !data ? (
          <ActivityIndicator color={pal.ink} size="large" style={{ marginTop: 24 }} />
        ) : !data?.hasSubscription && !hasActiveAccess ? (
          <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line }]}>
            <Text style={[s.kicker, { color: pal.mute, marginBottom: 8 }]}>No active subscription</Text>
            <Text style={{ color: pal.ink, fontSize: 15, lineHeight: 22 }}>
              Subscribe to VRC Member ($33/mo) to manage billing and Credits here.
            </Text>
            <TouchableOpacity
              style={[styles.primaryBtn, { backgroundColor: accent, marginTop: 16 }]}
              onPress={() => navigation.navigate('Plans')}
            >
              <Text style={{ color: '#fff', fontWeight: '600' }}>View Plans</Text>
            </TouchableOpacity>
          </View>
        ) : coveredByTourAdmin && !hasOwnSubscription ? (
          <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line }]}>
            <Text style={[s.kicker, { color: pal.mute, marginBottom: 8 }]}>Tour party crew access</Text>
            <Text style={{ color: pal.ink, fontSize: 15, lineHeight: 22 }}>
              Your Map and Calendar access is paid by your tour admin — you do not need your own VRC Member
              subscription
              {tourPartyCoverage?.periodEnd
                ? ` through ${formatDate(tourPartyCoverage.periodEnd)}`
                : ''}
              .
            </Text>
          </View>
        ) : (
          <>
            <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line }]}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <Text style={[s.kicker, { color: pal.mute }]}>Plan details</Text>
                <TouchableOpacity onPress={loadManageData} disabled={!!actionLoading || loading}>
                  <Text style={{ color: accent, fontWeight: '600', fontSize: 14 }}>
                    {loading ? 'Refreshing…' : 'Refresh'}
                  </Text>
                </TouchableOpacity>
              </View>
              <Text style={{ color: pal.ink, fontSize: 22, fontWeight: '600', marginTop: 8 }}>
                {data?.planName || plan?.name || 'Subscription'}
              </Text>
              <Text style={{ color: pal.mute, marginTop: 4 }}>{statusLabel(data)}</Text>
              {data?.effectivePriceCents != null ? (
                <Text style={{ color: pal.ink, marginTop: 10, fontSize: 15 }}>
                  Effective price {formatCentsAsDollars(data.effectivePriceCents)}/mo
                  {nextApplyCents > 0
                    ? ` (after ${formatCreditsAmount(nextApplyCents)})`
                    : ''}
                </Text>
              ) : null}
              <Text style={{ color: pal.mute, marginTop: 6, fontSize: 14 }}>
                Billing cycle {formatDate(data?.periodStart)} – {formatDate(data?.periodEnd)}
              </Text>
              {data?.planId === PLAN_IDS.VRC_MEMBER ? (
                <Text style={{ color: pal.mute, marginTop: 8, fontSize: 13.5, lineHeight: 20 }}>
                  Approved this cycle: {data.approvedCount ?? 0}. Credits from approvals bank in your
                  account — exchange to Wallet once per month.
                </Text>
              ) : null}
              {data?.cancelAtPeriodEnd && data?.cancelAt ? (
                <Text style={{ color: pal.warn, marginTop: 10, fontSize: 14 }}>
                  Access continues until {formatDate(data.cancelAt)}
                </Text>
              ) : null}

              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
                <TouchableOpacity
                  style={[s.outlineBtn, { borderColor: accent }]}
                  onPress={handleUpdatePayment}
                  disabled={!!actionLoading}
                >
                  {actionLoading === 'payment' ? (
                    <ActivityIndicator color={accent} size="small" />
                  ) : (
                    <Text style={{ color: accent, fontWeight: '600' }}>Update payment</Text>
                  )}
                </TouchableOpacity>
                <TouchableOpacity
                  style={[s.outlineBtn, { borderColor: accent }]}
                  onPress={() => navigation.navigate('Plans')}
                  disabled={!!actionLoading}
                >
                  <Text style={{ color: accent, fontWeight: '600' }}>Change plan</Text>
                </TouchableOpacity>
                {data?.cancelAtPeriodEnd ? (
                  <TouchableOpacity
                    style={[s.outlineBtn, { borderColor: accent, backgroundColor: accent }]}
                    onPress={handleResume}
                    disabled={!!actionLoading}
                  >
                    {actionLoading === 'resume' ? (
                      <ActivityIndicator color="#fff" size="small" />
                    ) : (
                      <Text style={{ color: '#fff', fontWeight: '600' }}>Resume</Text>
                    )}
                  </TouchableOpacity>
                ) : (
                  <TouchableOpacity
                    style={[s.outlineBtn, { borderColor: pal.alert }]}
                    onPress={handleCancel}
                    disabled={!!actionLoading}
                  >
                    {actionLoading === 'cancel' ? (
                      <ActivityIndicator color={pal.alert} size="small" />
                    ) : (
                      <Text style={{ color: pal.alert, fontWeight: '600' }}>Cancel at period end</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>
            </View>

            <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line }]}>
              <Text style={[s.kicker, { color: pal.mute, marginBottom: 12 }]}>Credits history</Text>
              {!creditsHistory.length ? (
                <Text style={{ color: pal.mute, fontSize: 14.5 }}>
                  No Credits activity yet. Approved contributions appear here.
                </Text>
              ) : (
                creditsHistory.map((entry) => {
                  const signed = creditsHistorySigned(entry);
                  return (
                    <View
                      key={entry.id}
                      style={[styles.histRow, { borderTopColor: pal.lineSoft }]}
                    >
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text style={{ color: pal.ink, fontSize: 14.5 }} numberOfLines={2}>
                          {entry.label || entry.type}
                        </Text>
                        <Text style={[s.meta, { color: pal.mute, marginTop: 2 }]}>
                          {formatDate(entry.createdAt)}
                          {entry.invoiceId ? ` · ${entry.invoiceId}` : ''}
                        </Text>
                      </View>
                      {signed !== 0 ? (
                        <Text
                          style={{
                            fontFamily: FONT.mono,
                            fontSize: 14,
                            fontWeight: '600',
                            color: signed > 0 ? accent : pal.alert,
                          }}
                        >
                          {signed > 0 ? '+' : '−'}
                          {formatCreditsAmount(Math.abs(signed))}
                        </Text>
                      ) : (
                        <Text style={[s.meta, { color: pal.mute }]}>
                          {formatCreditsAmount(entry.amountCents)}
                        </Text>
                      )}
                    </View>
                  );
                })
              )}
            </View>

            <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line }]}>
              <Text style={[s.kicker, { color: pal.mute, marginBottom: 12 }]}>Invoices</Text>
              {!data?.invoices?.length ? (
                <Text style={{ color: pal.mute, fontSize: 14.5 }}>No invoices yet.</Text>
              ) : (
                data.invoices.map((invoice) => (
                  <View
                    key={invoice.id}
                    style={[styles.histRow, { borderTopColor: pal.lineSoft }]}
                  >
                    <View style={{ flex: 1 }}>
                      <Text style={{ color: pal.ink, fontWeight: '600' }}>
                        {formatDate(invoice.created)}
                      </Text>
                      <Text style={{ color: pal.mute, fontSize: 13.5, marginTop: 2 }}>
                        Paid {formatCentsAsDollars(invoice.amountPaid ?? invoice.total ?? 0)}
                        {invoice.status ? ` · ${invoice.status}` : ''}
                      </Text>
                      {(invoice.contributionDiscountCents || 0) > 0 ? (
                        <Text style={{ color: accent, fontSize: 13, marginTop: 4 }}>
                          −{formatCreditsAmount(invoice.contributionDiscountCents)}
                        </Text>
                      ) : null}
                    </View>
                    {(invoice.hostedInvoiceUrl || invoice.invoicePdf) ? (
                      <TouchableOpacity onPress={() => openInvoice(invoice)}>
                        <Text style={{ color: accent, fontWeight: '600' }}>View</Text>
                      </TouchableOpacity>
                    ) : null}
                  </View>
                ))
              )}
            </View>
          </>
        )}
      </ScrollView>
    </View>
  );
}

const s = StyleSheet.create({
  label: { fontFamily: FONT.mono, fontSize: 12, letterSpacing: 1.2, textTransform: 'uppercase' },
  meta: { fontFamily: FONT.mono, fontSize: 11.5 },
  kicker: { fontFamily: FONT.mono, fontSize: 12.5, letterSpacing: 0.7, textTransform: 'uppercase' },
  hero: { fontSize: 36, fontWeight: '700', letterSpacing: -0.6 },
  card: { borderWidth: 1, borderRadius: 12, padding: 18, marginBottom: 16 },
  outlineBtn: { paddingVertical: 7, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1 },
});

const styles = StyleSheet.create({
  page: { flex: 1 },
  pageInner: {
    padding: 24,
    paddingBottom: 48,
    maxWidth: 980,
    width: '100%',
    alignSelf: 'center',
  },
  banner: {
    padding: 12,
    borderRadius: 9,
    marginBottom: 14,
  },
  heroRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
    marginBottom: 20,
    paddingBottom: 18,
    borderBottomWidth: 2,
  },
  accentPanel: {
    flex: 1,
    minWidth: 280,
    maxWidth: 380,
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderLeftWidth: 3,
    overflow: 'hidden',
  },
  progressTrack: {
    height: 6,
    borderRadius: 3,
    marginTop: 12,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
  },
  primaryBtn: {
    alignSelf: 'flex-start',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  histRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    paddingVertical: 12,
    borderTopWidth: 1,
  },
});
